import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  collectionLabelsMatchPersonalizationType,
  getKaPersonalizationTypeLabelRequirement,
} from '../../../../../constants/collections/KAActionBucketPageDataLabels';
import { getAgentCollectionLabels } from '../../../../../constants/agents/agentCollectionHelpers';
import {
  hasEmptyWebhookFieldConfig,
  isWebhookIntegrationEnabled,
} from '../../components/design/controllers/webhookConfigUtils';
import { useGetAllUserCtasQuery, useGetFAQKeywordConfigQuery } from '../../../../../services/api';
import { getFAQKeywordProblem } from '../../../../../constants/agents/faqKeywords';

const useAgentDesignValidator = () => {
  const agent = useSelector(state => state.agent.data);
  const gatedEngagement = useSelector(state => state.agent.data.campaignData.gatedEngagement);
  const engineId = useSelector(state => state.agent.data.campaignData.engineId);
  const engagementGenerationByAI = useSelector(
    state => state.agent.data.campaignData.engagementGenerationByAI
  );
  const placements = useSelector(state => state?.agent?.data?.placements);
  const pollCtaMapping = useSelector(state => state.agent.etc.pollCtaMapping);
  const webhookConfig = useSelector(
    state => state.agent.data.campaignData.ctaWebhookIntegrationConfig || []
  );

  const { data: ctaResponse } = useGetAllUserCtasQuery();
  const allCtas = ctaResponse?.data || [];
  // FAQ keywords: if the config can't load, the keyword check doesn't block setup.
  const { data: faqKeywordConfigResponse } = useGetFAQKeywordConfigQuery(undefined, {
    skip: agent?.campaignData?.campaignType !== 'rag',
  });
  const pollCTAsUsageType = useSelector(state => state.agent.etc.pollCtaMapping.pollCTAsUsageType);
  const ABtestActions = useSelector(state => state.agent.etc.pollCtaMapping.ABtestActions);
  const mappedOptions = useSelector(state => state.agent.etc.pollCtaMapping.mappedOptions);

  const hasPollCtaMappingCTAs = () => {
    if (pollCTAsUsageType === 'abTest') {
      return ABtestActions?.some(action => action?._id);
    }
    if (pollCTAsUsageType === 'mapping') {
      return mappedOptions?.some(option => option?.actionIds?.[0]?._id);
    }
    return false;
  };

  const isAgentDesignNextButtonDisabled = () => {
    function hasEmptyQuestionText() {
      return agent?.campaignData?.xrayQuestions?.some(
        obj => obj.isIncluded && obj.questionText.trim() === ''
      );
    }
    const campaignType = agent?.campaignData?.campaignType;
    const hasNoCTAs =
      campaignType === 'recirculation'
        ? !agent?.campaignData?.actions?.some(action => action?.actionData?.actionType === 'link')
        : campaignType === 'pollingAgent' || campaignType === 'engagementOnly'
          ? !agent?.campaignData?.actions?.length && !hasPollCtaMappingCTAs()
          : !agent?.campaignData?.actions?.length;
    const hasNoActionBucketId = !agent?.campaignData?.actionBucketId?._id;
    const hasNoActionBucketIds = !agent?.campaignData?.actionBucketIds?.length;
    const isEngEmpty = agent?.campaignData?.engagements?.length === 0;
    const engagementTitle = agent?.campaignData?.engagementTitle;
    const xrayQuestions = agent?.campaignData?.xrayQuestions;
    const feedbackConfig = agent?.campaignData?.feedbackConfig;
    const endScreen = agent?.campaignData?.endScreen?._id;
    const personalizationTool = agent?.campaignData?.personalizationTool;

    // placements
    // Parent validation block for htmlElement placements
    const stringPatternPlacements = placements.filter(
      p => p?.placementData?.type === 'stringPattern'
    );
    const htmlElementPlacements = placements.filter(p => p?.placementData?.type === 'htmlElement');

    // these are the validations for placements

    if (placements?.length > 0) {
      // check if the placement type is string pattern and the string pattern is empty
      if (
        stringPatternPlacements.some(
          placement =>
            placement?.placementData?.type === 'stringPattern' &&
            !placement?.placementData?.stringPattern
        )
      ) {
        toast.warning('Enter a string pattern for the placement');
        return true;
      }

      // check if the placement type is html element and the html element is empty
      if (htmlElementPlacements?.length > 0) {
        // findBy = id → elementId required
        if (
          htmlElementPlacements.some(
            p => p?.placementData?.findBy === 'id' && !p?.placementData?.elementId
          )
        ) {
          toast.warning('Enter the element ID for the placement');
          return true;
        }

        // findBy = name → elementName required
        if (
          htmlElementPlacements.some(
            p => p?.placementData?.findBy === 'name' && !p?.placementData?.elementName
          )
        ) {
          toast.warning('Enter the element name for the placement');
          return true;
        }

        // findBy = customAttribute → all custom fields required
        if (
          htmlElementPlacements.some(p => {
            const d = p?.placementData;

            const isInvalidOccurrences =
              d?.customAtributeNumberOfOccurences === null ||
              d?.customAtributeNumberOfOccurences === undefined;

            return (
              d?.findBy === 'customAttribute' &&
              (!d?.customAttributeKey || !d?.customAttributeValue || isInvalidOccurrences)
            );
          })
        ) {
          toast.warning('Fill in every custom attribute field for the placement');
          return true;
        }
      }

      //check if the placement type is disabled and the disabled is true
    }

    if (campaignType !== 'xRay' && campaignType !== 'recirculation') {
      if (gatedEngagement && hasNoCTAs) {
        toast.warning('Add a CTA to continue');
        return true;
      }
    }
    if (
      (campaignType === 'rag' ||
        campaignType === 'DEA' ||
        (campaignType === 'pollingAgent' && engagementGenerationByAI)) &&
      !engineId?._id
    ) {
      toast.warning('Pick an engine');
      return true;
    }

    if (feedbackConfig?.isActive) {
      if (feedbackConfig?.title?.trim() === '') {
        toast.warning('Enter a title for the feedback');
        return true;
      }
    }

    if (campaignType === 'recirculation') {
      if (hasNoCTAs && hasNoActionBucketId) {
        toast.warning('Add a collection or a CTA to continue');
        return true;
      }

      return false;
    }

    if (campaignType === 'DEA') {
      if (engagementTitle === '') {
        toast.warning('Enter a title for the engagement');
        return true;
      }
    }

    if (campaignType === 'engagementOnly' || campaignType === 'pollingAgent') {
      // if (!endScreen) {
      //   notification.warning({
      //     message: 'Please Select a End Screen',
      //     placement: 'topRight',
      //     showProgress: true,
      //   });
      //   return true;
      // }
      if (!agent?.campaignData?.engagementGenerationByAI) {
        if (isEngEmpty && hasNoCTAs) {
          toast.warning('Add an engagement or a CTA to continue');
          return true;
        }
      }

      return false;
    }

    if (campaignType === 'rag') {
      if (hasNoActionBucketIds) {
        toast.warning('Add at least one collection');
        return true;
      }

      if (engagementTitle === '') {
        toast.warning('Enter a title for the engagement');
        return true;
      }

      if (personalizationTool?.isActive) {
        const { userInputCTAId } = personalizationTool?.ctasConfig ?? {
          userInputCTAId: personalizationTool?.userInputCTAId,
        };
        const isPersonalizationCtaMissing = cta => {
          if (cta == null || cta === '') return true;
          if (typeof cta === 'string') return cta.trim() === '';
          if (typeof cta === 'object') return !cta?._id;
          return true;
        };

        if (isPersonalizationCtaMissing(userInputCTAId)) {
          toast.warning('Pick a user input CTA');
          return true;
        }

        const ptCollections = personalizationTool?.actionBucketIds;
        if (!Array.isArray(ptCollections) || ptCollections.length === 0) {
          toast.warning('Pick at least one collection for the personalisation tool');
          return true;
        }

        const requiredFields = personalizationTool?.requiredFields;
        if (!Array.isArray(requiredFields) || requiredFields.length === 0) {
          toast.warning('Pick at least one required field for the personalisation tool');
          return true;
        }

        const labelRequirement = getKaPersonalizationTypeLabelRequirement(
          personalizationTool?.type
        );

        if (labelRequirement) {
          const { groupTitle } = labelRequirement;
          // A default label of the type's category, or one of the team's labels for it.
          const hasCollectionWithMatchingLabels = ptCollections.some(bucket =>
            collectionLabelsMatchPersonalizationType(
              getAgentCollectionLabels(bucket),
              personalizationTool?.type
            )
          );

          if (!hasCollectionWithMatchingLabels) {
            toast.warning(
              `Pick at least one collection with a ${groupTitle} label to match the personalisation type`,
              { duration: 3000 }
            );
            return true;
          }
        }

        const hasExportResult =
          personalizationTool?.ctasConfig?.hasExportResult ?? personalizationTool?.hasExportResult;
        if (hasExportResult) {
          const exportEmailConfig = personalizationTool?.exportEmailConfig;
          const { provider, mailbox, clientId, clientSecret, authority } = exportEmailConfig ?? {};

          if (provider === 'graphAPI') {
            if (!mailbox || !clientId || !clientSecret || !authority) {
              toast.warning('Fill in every export email field');
              return true;
            }
          }
        }
      }

      const faqKeywordProblem = getFAQKeywordProblem(
        agent?.campaignData?.ragConfig?.articleRelatedQuestions,
        faqKeywordConfigResponse?.data
      );
      if (faqKeywordProblem) {
        toast.warning(faqKeywordProblem);
        return true;
      }

      // // Validate intents - check if engine has intents attached
      // const selectedEngine = engineList?.find(engine => engine?._id === engineId?._id);
      // const engineIntents = selectedEngine?.engineData?.intents;
      // const hasEngineIntents = engineIntents && engineIntents.length > 0;

      // if (hasEngineIntents) {
      //   // Get no_intent_detected and other intents separately
      //   const noIntentDetected = linkedIntents?.find(
      //     linkedIntent => linkedIntent.intent === 'no_intent_detected'
      //   );
      //   const otherIntents = linkedIntents?.filter(
      //     linkedIntent => linkedIntent.intent !== 'no_intent_detected'
      //   );

      //   // Check if no_intent_detected doesn't have actionId
      //   if (noIntentDetected && !noIntentDetected.actionId) {
      //     notification.warning({
      //       message: 'Please attach a CTA to the "No intent detected" option',
      //       placement: 'topRight',
      //       duration: 2,
      //       showProgress: true,
      //     });
      //     return true;
      //   }

      //   // Check if other intents don't have actionId
      //   // Only show warning if no_intent_detected also doesn't have actionId
      //   // (If only no_intent_detected has actionId, that's valid)
      //   const otherIntentsWithoutActionId = otherIntents?.filter(intent => !intent.actionId);

      //   if (
      //     otherIntentsWithoutActionId &&
      //     otherIntentsWithoutActionId.length > 0 &&
      //     (!noIntentDetected || !noIntentDetected.actionId)
      //   ) {
      //     notification.warning({
      //       message: 'Please attach a CTA to an intent',
      //       placement: 'topRight',
      //       duration: 2,
      //       showProgress: true,
      //     });
      //     return true;
      //   }
      // }

      if (
        isWebhookIntegrationEnabled(agent?.campaignData, pollCtaMapping, allCtas) &&
        hasEmptyWebhookFieldConfig(webhookConfig)
      ) {
        toast.warning('Fill in every webhook field');
        return true;
      }

      return false;
    }

    if (campaignType === 'xRay') {
      if (xrayQuestions && Array.isArray(xrayQuestions)) {
        const excludedTypes = ['relatedTopics', 'linkActions', 'relatedAuthors'];
        const hasMissingGuideline = xrayQuestions.some(
          question =>
            question.isIncluded &&
            !excludedTypes.includes(question.type) &&
            (!question.userGuideline || question.userGuideline.trim() === '')
        );

        if (hasMissingGuideline) {
          toast.warning('Add a user guideline to every included question');
          return true;
        }
      }
      if (hasEmptyQuestionText()) {
        toast.warning('Fill in the text for every selected question');
        return true;
      }

      if (
        xrayQuestions?.some(
          question =>
            (question.type === 'relatedAuthors' || question.type === 'relatedTopics') &&
            question.isIncluded
        )
      ) {
        if (!agent?.campaignData?.actionBucketId?._id) {
          toast.warning('Add a collection to continue');
          return true;
        }
      }

      if (xrayQuestions?.some(question => question.type === 'linkActions' && question.isIncluded)) {
        if (
          !agent?.campaignData?.aiSimilarityActionBucketId?._id &&
          !agent?.campaignData?.linkActions?.length
        ) {
          toast.warning('Add an articles collection or link actions');
          return true;
        }

        if (
          agent?.campaignData?.aiSimilarityActionBucketId?._id &&
          agent?.campaignData?.linkActions?.length
        ) {
          toast.warning('Use an articles collection or link actions, not both');
          return true;
        }
      }

      return false;
    }

    return false;
  };

  return { isAgentDesignNextButtonDisabled };
};

export default useAgentDesignValidator;
